import JSZip from "jszip";
import { attr, child, find, findAll, kids, parseXml, type XNode } from "./xmlLite";
import type { Block, Border, BorderSet, Cell, DocxDoc, DocxWarnings, Inline, ListLabel, Para, ParaProps, Row, RunProps, Section, Table, TabStop } from "./docxModel";

/**
 * Reads a .docx into the small document model in docxModel.ts: paragraphs with their fully resolved
 * style (document defaults → paragraph style → character style → direct formatting), lists with their
 * real numbers, tables, pictures, hyperlinks, page breaks, sections with page size and margins,
 * headers and footers (with PAGE / NUMPAGES fields) and footnotes. Pure apart from reading the zip.
 */

export type ImageConverter = (data: Uint8Array, ext: string) => Promise<Uint8Array | null>;

export interface ReadOptions {
  /** Turns GIF / BMP / WebP … into PNG bytes (browser only); null when it cannot. */
  convertImage?: ImageConverter;
}

export class NotDocxError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotDocxError";
  }
}

const tw = (v: string | undefined) => (v === undefined || v === "" || isNaN(Number(v)) ? undefined : Number(v) / 20);
const on = (n: XNode | undefined): boolean | undefined => {
  if (!n) return undefined;
  const v = attr(n, "w:val");
  if (v === undefined) return true;
  return !(v === "0" || v === "false" || v === "off");
};
const hexColor = (v: string | undefined) => (v && /^[0-9a-fA-F]{6}$/.test(v) ? v.toUpperCase() : undefined);

const HIGHLIGHT: Record<string, string> = {
  yellow: "FFFF00", green: "00FF00", cyan: "00FFFF", magenta: "FF00FF", blue: "0000FF", red: "FF0000", darkBlue: "000080",
  darkCyan: "008080", darkGreen: "008000", darkMagenta: "800080", darkRed: "800000", darkYellow: "808000", darkGray: "808080",
  lightGray: "C0C0C0", black: "000000", white: "FFFFFF",
};

const DEFAULT_PARA: ParaProps = {
  align: "left", beforePt: 0, afterPt: 0, line: { value: 1, rule: "auto" }, leftPt: 0, rightPt: 0, firstPt: 0,
  keepNext: false, keepLines: false, pageBreakBefore: false, contextualSpacing: false, widowControl: true, borders: {}, tabs: [],
};

// ---------------------------------------------------------------------------------------------
// properties
// ---------------------------------------------------------------------------------------------

interface ThemeFonts {
  major?: string;
  minor?: string;
}

function applyRPr(target: RunProps, rPr: XNode | undefined, theme: ThemeFonts) {
  if (!rPr) return;
  for (const c of rPr.children) {
    switch (c.name) {
      case "w:b": target.bold = on(c); break;
      case "w:i": target.italic = on(c); break;
      case "w:strike": case "w:dstrike": target.strike = on(c); break;
      case "w:caps": target.caps = on(c); break;
      case "w:smallCaps": target.smallCaps = on(c); break;
      case "w:vanish": target.hidden = on(c); break;
      case "w:u": { const v = attr(c, "w:val"); target.underline = v !== "none" && v !== "0"; break; }
      case "w:color": { const col = hexColor(attr(c, "w:val")); if (col) target.color = col; else if (attr(c, "w:val") === "auto") target.color = undefined; break; }
      case "w:sz": { const v = Number(attr(c, "w:val")); if (v > 0) target.sizePt = v / 2; break; }
      case "w:highlight": { const v = attr(c, "w:val"); if (v && v !== "none") target.background = HIGHLIGHT[v]; else if (v === "none") target.background = undefined; break; }
      case "w:shd": { const f = hexColor(attr(c, "w:fill")); if (f) target.background = f; break; }
      case "w:vertAlign": { const v = attr(c, "w:val"); target.vert = v === "superscript" ? "sup" : v === "subscript" ? "sub" : undefined; break; }
      case "w:rFonts": {
        const direct = attr(c, "w:ascii", "w:hAnsi", "w:cs");
        const themed = attr(c, "w:asciiTheme", "w:hAnsiTheme");
        if (direct) target.font = direct;
        else if (themed) target.font = /major/i.test(themed) ? theme.major : theme.minor;
        break;
      }
    }
  }
}

function parseBorder(n: XNode | undefined): Border | undefined {
  if (!n) return undefined;
  const style = attr(n, "w:val");
  if (!style || style === "nil" || style === "none") return undefined;
  const sz = Number(attr(n, "w:sz"));
  return { style, sizePt: sz > 0 ? sz / 8 : 0.5, color: hexColor(attr(n, "w:color")) ?? "000000" };
}

function applyPPr(target: ParaProps, pPr: XNode | undefined) {
  if (!pPr) return;
  for (const c of pPr.children) {
    switch (c.name) {
      case "w:jc": {
        const v = attr(c, "w:val");
        target.align = v === "center" ? "center" : v === "right" || v === "end" ? "right" : v === "both" || v === "distribute" || v === "justify" ? "justify" : "left";
        break;
      }
      case "w:spacing": {
        const b = tw(attr(c, "w:before")); if (b !== undefined) target.beforePt = b;
        const a = tw(attr(c, "w:after")); if (a !== undefined) target.afterPt = a;
        const line = Number(attr(c, "w:line"));
        if (line > 0) {
          const rule = attr(c, "w:lineRule");
          target.line = rule === "exact" ? { value: line / 20, rule: "exact" } : rule === "atLeast" ? { value: line / 20, rule: "atLeast" } : { value: line / 240, rule: "auto" };
        }
        break;
      }
      case "w:ind": {
        const left = tw(attr(c, "w:left", "w:start")); if (left !== undefined) target.leftPt = left;
        const right = tw(attr(c, "w:right", "w:end")); if (right !== undefined) target.rightPt = right;
        const hang = tw(attr(c, "w:hanging")); const first = tw(attr(c, "w:firstLine"));
        if (hang !== undefined) target.firstPt = -hang;
        else if (first !== undefined) target.firstPt = first;
        break;
      }
      case "w:keepNext": target.keepNext = on(c) ?? true; break;
      case "w:keepLines": target.keepLines = on(c) ?? true; break;
      case "w:pageBreakBefore": target.pageBreakBefore = on(c) ?? true; break;
      case "w:contextualSpacing": target.contextualSpacing = on(c) ?? true; break;
      case "w:widowControl": target.widowControl = on(c) ?? true; break;
      case "w:shd": { const f = hexColor(attr(c, "w:fill")); target.shade = f; break; }
      case "w:pBdr": {
        const b: BorderSet = {};
        for (const [k, name] of [["top", "w:top"], ["bottom", "w:bottom"], ["left", "w:left"], ["right", "w:right"]] as const) {
          const v = parseBorder(child(c, name));
          if (v) b[k] = v;
        }
        target.borders = b;
        break;
      }
      case "w:tabs": {
        const stops: TabStop[] = [...target.tabs];
        for (const t of kids(c, "w:tab")) {
          const pos = tw(attr(t, "w:pos"));
          if (pos === undefined) continue;
          const val = attr(t, "w:val");
          const idx = stops.findIndex((s) => Math.abs(s.posPt - pos) < 0.5);
          if (val === "clear") { if (idx >= 0) stops.splice(idx, 1); continue; }
          const stop: TabStop = { posPt: pos, align: val === "center" ? "center" : val === "right" || val === "end" ? "right" : val === "decimal" ? "decimal" : "left" };
          if (idx >= 0) stops[idx] = stop; else stops.push(stop);
        }
        target.tabs = stops.sort((a, b) => a.posPt - b.posPt);
        break;
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------------------------

interface StyleDef {
  id: string;
  type: string;
  name: string;
  basedOn?: string;
  pPr?: XNode;
  rPr?: XNode;
  tblPr?: XNode;
}

class Styles {
  byId = new Map<string, StyleDef>();
  defaultPara?: string;
  docP: ParaProps = { ...DEFAULT_PARA, borders: {}, tabs: [] };
  docR: RunProps = { sizePt: 11, font: "Times New Roman" }; // Word's own default when a file names no font
  constructor(xml: XNode | undefined, private theme: ThemeFonts) {
    if (!xml) return;
    const dd = child(xml, "w:docDefaults");
    applyRPr(this.docR, child(child(dd, "w:rPrDefault"), "w:rPr"), theme);
    applyPPr(this.docP, child(child(dd, "w:pPrDefault"), "w:pPr"));
    for (const s of kids(xml, "w:style")) {
      const id = attr(s, "w:styleId");
      if (!id) continue;
      const def: StyleDef = {
        id, type: attr(s, "w:type") ?? "paragraph", name: attr(child(s, "w:name"), "w:val") ?? id,
        basedOn: attr(child(s, "w:basedOn"), "w:val"), pPr: child(s, "w:pPr"), rPr: child(s, "w:rPr"), tblPr: child(s, "w:tblPr"),
      };
      this.byId.set(id, def);
      if (def.type === "paragraph" && attr(s, "w:default") === "1") this.defaultPara = id;
    }
  }
  chain(id: string | undefined): StyleDef[] {
    const out: StyleDef[] = [];
    const seen = new Set<string>();
    let cur = id ? this.byId.get(id) : undefined;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      out.unshift(cur);
      cur = cur.basedOn ? this.byId.get(cur.basedOn) : undefined;
    }
    return out;
  }
  /** Paragraph + run properties of a paragraph style (document defaults included). */
  paragraph(id: string | undefined): { p: ParaProps; r: RunProps; styles: StyleDef[] } {
    const p: ParaProps = { ...this.docP, borders: { ...this.docP.borders }, tabs: [...this.docP.tabs] };
    const r: RunProps = { ...this.docR };
    const styles = this.chain(id ?? this.defaultPara);
    for (const s of styles) {
      applyPPr(p, s.pPr);
      applyRPr(r, s.rPr, this.theme);
    }
    return { p, r, styles };
  }
  character(id: string | undefined): RunProps {
    const r: RunProps = {};
    for (const s of this.chain(id)) applyRPr(r, s.rPr, this.theme);
    return r;
  }
  tableBorders(id: string | undefined): Table["borders"] {
    const out: Table["borders"] = {};
    for (const s of this.chain(id)) mergeTblBorders(out, child(s.tblPr, "w:tblBorders"));
    return out;
  }
}

function mergeTblBorders(out: Table["borders"], b: XNode | undefined) {
  if (!b) return;
  for (const [k, names] of [["top", ["w:top"]], ["bottom", ["w:bottom"]], ["left", ["w:left", "w:start"]], ["right", ["w:right", "w:end"]], ["insideH", ["w:insideH"]], ["insideV", ["w:insideV"]]] as const) {
    for (const nm of names) {
      const node = child(b, nm);
      if (!node) continue;
      const v = parseBorder(node);
      if (v) out[k] = v; else delete out[k];
    }
  }
}

// ---------------------------------------------------------------------------------------------
// numbering
// ---------------------------------------------------------------------------------------------

interface NumLevel { start: number; fmt: string; text: string; pPr?: XNode; rPr?: XNode }

function toRoman(n: number): string {
  const map: [number, string][] = [[1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let s = "";
  for (const [v, r] of map) while (n >= v) { s += r; n -= v; }
  return s;
}
function toLetters(n: number): string {
  let s = "";
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(97 + ((n - 1) % 26)) + s;
  return s;
}

export function formatNumber(n: number, fmt: string): string {
  switch (fmt) {
    case "lowerLetter": return toLetters(n);
    case "upperLetter": return toLetters(n).toUpperCase();
    case "lowerRoman": return toRoman(n).toLowerCase();
    case "upperRoman": return toRoman(n);
    case "decimalZero": return n < 10 ? `0${n}` : String(n);
    case "ordinal": { const s = ["th", "st", "nd", "rd"]; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }
    case "none": return "";
    default: return String(n);
  }
}

class Numbering {
  private abstracts = new Map<string, Map<number, NumLevel>>();
  private nums = new Map<string, { abstractId: string; overrides: Map<number, number> }>();
  private counters = new Map<string, (number | undefined)[]>();
  constructor(xml: XNode | undefined) {
    if (!xml) return;
    for (const a of kids(xml, "w:abstractNum")) {
      const id = attr(a, "w:abstractNumId");
      if (id === undefined) continue;
      const levels = new Map<number, NumLevel>();
      for (const l of kids(a, "w:lvl")) {
        const il = Number(attr(l, "w:ilvl") ?? 0);
        levels.set(il, {
          start: Number(attr(child(l, "w:start"), "w:val") ?? 1),
          fmt: attr(child(l, "w:numFmt"), "w:val") ?? "decimal",
          text: attr(child(l, "w:lvlText"), "w:val") ?? "",
          pPr: child(l, "w:pPr"),
          rPr: child(l, "w:rPr"),
        });
      }
      this.abstracts.set(id, levels);
    }
    for (const n of kids(xml, "w:num")) {
      const id = attr(n, "w:numId");
      const abs = attr(child(n, "w:abstractNumId"), "w:val");
      if (id === undefined || abs === undefined) continue;
      const overrides = new Map<number, number>();
      for (const o of kids(n, "w:lvlOverride")) {
        const so = child(o, "w:startOverride");
        if (so) overrides.set(Number(attr(o, "w:ilvl") ?? 0), Number(attr(so, "w:val") ?? 1));
      }
      this.nums.set(id, { abstractId: abs, overrides });
    }
  }
  level(numId: string, ilvl: number): NumLevel | undefined {
    const n = this.nums.get(numId);
    return n ? this.abstracts.get(n.abstractId)?.get(ilvl) : undefined;
  }
  /** Advances the counter for this list and level; returns the label text. */
  next(numId: string, ilvl: number): { text: string; bullet: boolean; level: NumLevel } | undefined {
    const n = this.nums.get(numId);
    const lvl = this.level(numId, ilvl);
    if (!n || !lvl) return undefined;
    if (lvl.fmt === "bullet") return { text: bulletChar(lvl.text), bullet: true, level: lvl };
    const counters = this.counters.get(numId) ?? [];
    const startFor = (l: number) => n.overrides.get(l) ?? this.level(numId, l)?.start ?? 1;
    counters[ilvl] = counters[ilvl] === undefined ? startFor(ilvl) : (counters[ilvl] as number) + 1;
    for (let l = ilvl + 1; l < counters.length; l++) counters[l] = undefined;
    this.counters.set(numId, counters);
    const text = lvl.text.replace(/%(\d)/g, (_, d: string) => {
      const l = Number(d) - 1;
      const lv = this.level(numId, l);
      const value = counters[l] ?? startFor(l);
      return formatNumber(value, lv?.fmt ?? "decimal");
    });
    return { text, bullet: false, level: lvl };
  }
}

function bulletChar(t: string): string {
  if (!t) return "•";
  const cp = t.codePointAt(0)!;
  if (cp >= 0xf000 && cp <= 0xf0ff) return "•"; // Symbol / Wingdings private-use bullets
  if (t === "o") return "–";
  return t;
}

// ---------------------------------------------------------------------------------------------
// the reader
// ---------------------------------------------------------------------------------------------

interface PartCtx {
  rels: Map<string, { target: string; external: boolean }>;
  /** Where the part lives (for resolving relative targets). */
  dir: string;
}

interface FieldState {
  instr: string;
  mode: "instr" | "result";
  skipResult: boolean;
  link?: string;
}

function guessType(data: Uint8Array): "png" | "jpg" | "other" {
  if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) return "png";
  if (data[0] === 0xff && data[1] === 0xd8) return "jpg";
  return "other";
}

class Reader {
  styles!: Styles;
  numbering!: Numbering;
  theme: ThemeFonts = {};
  media = new Map<string, Uint8Array>();
  warnings: DocxWarnings = { textBoxes: 0, pictures: 0, columnSections: 0, charts: 0 };
  footnoteXml = new Map<string, XNode>();
  usedFootnotes: { id: string; label: string }[] = [];
  private fields: FieldState[] = [];
  private partCache = new Map<string, Block[]>();

  constructor(private zip: JSZip, private opts: ReadOptions) {}

  async text(path: string): Promise<string | undefined> {
    const f = this.zip.file(path);
    return f ? f.async("string") : undefined;
  }

  async relsFor(partPath: string): Promise<PartCtx> {
    const slash = partPath.lastIndexOf("/");
    const dir = partPath.slice(0, slash + 1);
    const xml = await this.text(`${dir}_rels/${partPath.slice(slash + 1)}.rels`);
    const rels = new Map<string, { target: string; external: boolean }>();
    if (xml) {
      for (const r of kids(parseXml(xml), "Relationship")) {
        const id = attr(r, "Id");
        const target = attr(r, "Target");
        if (id && target) rels.set(id, { target, external: attr(r, "TargetMode") === "External" });
      }
    }
    return { rels, dir };
  }

  resolve(ctx: PartCtx, target: string): string {
    if (target.startsWith("/")) return target.slice(1);
    const parts = (ctx.dir + target).split("/");
    const out: string[] = [];
    for (const p of parts) {
      if (p === "..") out.pop();
      else if (p && p !== ".") out.push(p);
    }
    return out.join("/");
  }

  async init() {
    const themeXml = await this.text("word/theme/theme1.xml");
    if (themeXml) {
      const t = parseXml(themeXml);
      this.theme = {
        major: attr(child(find(t, "a:majorFont"), "a:latin"), "typeface"),
        minor: attr(child(find(t, "a:minorFont"), "a:latin"), "typeface"),
      };
    }
    const stylesXml = await this.text("word/styles.xml");
    this.styles = new Styles(stylesXml ? parseXml(stylesXml) : undefined, this.theme);
    const numXml = await this.text("word/numbering.xml");
    this.numbering = new Numbering(numXml ? parseXml(numXml) : undefined);
    const fnXml = await this.text("word/footnotes.xml");
    if (fnXml) for (const f of kids(parseXml(fnXml), "w:footnote")) {
      const id = attr(f, "w:id");
      const type = attr(f, "w:type");
      if (id !== undefined && !type) this.footnoteXml.set(id, f);
    }
    // Media is read up front so the paragraph parser can stay synchronous.
    const jobs: Promise<void>[] = [];
    this.zip.forEach((path, entry) => {
      if (!entry.dir && /^word\/media\//i.test(path)) jobs.push(entry.async("uint8array").then((d) => { this.media.set(path, d); }));
    });
    await Promise.all(jobs);
  }

  // ---- blocks -------------------------------------------------------------------------------

  blocks(container: XNode, ctx: PartCtx, onSection?: (sect: XNode, blocks: Block[]) => void): Block[] {
    const out: Block[] = [];
    const walk = (node: XNode) => {
      for (const c of node.children) {
        if (c.name === "w:p") {
          out.push(this.paragraph(c, ctx));
          const sect = child(child(c, "w:pPr"), "w:sectPr");
          if (sect && onSection) onSection(sect, out.splice(0, out.length));
        } else if (c.name === "w:tbl") out.push(this.table(c, ctx));
        else if (c.name === "w:sdt") walk(child(c, "w:sdtContent") ?? c);
        else if (c.name === "w:customXml" || c.name === "w:ins" || c.name === "w:moveTo") walk(c);
        else if (c.name === "mc:AlternateContent") walk(child(c, "mc:Choice") ?? c);
      }
    };
    walk(container);
    return out;
  }

  paragraph(p: XNode, ctx: PartCtx): Para {
    const pPr = child(p, "w:pPr");
    const styleId = attr(child(pPr, "w:pStyle"), "w:val");
    const { p: props, r: base, styles } = this.styles.paragraph(styleId);
    // List numbering: from the paragraph or from its style.
    let numId = attr(child(child(pPr, "w:numPr"), "w:numId"), "w:val");
    let ilvl = Number(attr(child(child(pPr, "w:numPr"), "w:ilvl"), "w:val") ?? NaN);
    for (const s of styles) {
      const np = child(s.pPr, "w:numPr");
      if (np) {
        if (numId === undefined) numId = attr(child(np, "w:numId"), "w:val");
        if (isNaN(ilvl)) ilvl = Number(attr(child(np, "w:ilvl"), "w:val") ?? NaN);
      }
    }
    if (isNaN(ilvl)) ilvl = 0;
    let list: ListLabel | undefined;
    if (numId && numId !== "0") {
      const label = this.numbering.next(numId, ilvl);
      if (label) {
        // The list level sets the indent; the paragraph's own indent (applied below) wins over it.
        applyPPr(props, label.level.pPr);
        const lp: RunProps = { ...base };
        applyRPr(lp, label.level.rPr, this.theme);
        list = { text: label.text, bullet: label.bullet, p: lp };
      }
    }
    applyPPr(props, pPr);
    // Re-apply the direct indent over the numbering indent without touching other properties.
    const markProps: RunProps = { ...base };
    applyRPr(markProps, child(pPr, "w:rPr"), this.theme);
    if (list) list.p = { ...list.p, ...(child(pPr, "w:rPr") ? { sizePt: markProps.sizePt, bold: markProps.bold ?? list.p.bold } : {}) };

    const inlines: Inline[] = [];
    this.inlineContainer(p, ctx, base, inlines, undefined);
    const out: Para = { kind: "p", inlines, props, base: markProps, list, styleId: styleId ?? this.styles.defaultPara };
    const named = this.styles.byId.get(styleId ?? "")?.name ?? "";
    const ol = attr(child(pPr, "w:outlineLvl"), "w:val") ?? attr(child(this.styles.byId.get(styleId ?? "")?.pPr, "w:outlineLvl"), "w:val");
    const m = /^heading\s*(\d)/i.exec(named);
    if (ol !== undefined && Number(ol) < 9) out.outline = Number(ol) + 1;
    else if (m) out.outline = Number(m[1]);
    else if (/^title$/i.test(named)) out.outline = 1;
    return out;
  }

  private push(inlines: Inline[], item: Inline) {
    const last = inlines[inlines.length - 1];
    if (item.t === "text" && last && last.t === "text" && last.link === item.link && JSON.stringify(last.p) === JSON.stringify(item.p)) {
      last.text += item.text;
      return;
    }
    inlines.push(item);
  }

  private skipping(): boolean {
    return this.fields.some((f) => f.mode === "instr" || f.skipResult);
  }

  private inlineContainer(node: XNode, ctx: PartCtx, base: RunProps, out: Inline[], link: string | undefined) {
    for (const c of node.children) {
      switch (c.name) {
        case "w:r": this.run(c, ctx, base, out, link); break;
        case "w:hyperlink": {
          const rid = attr(c, "r:id");
          const target = rid ? ctx.rels.get(rid) : undefined;
          this.inlineContainer(c, ctx, base, out, target?.external ? target.target : link);
          break;
        }
        case "w:ins": case "w:smartTag": case "w:customXml": case "w:moveTo": case "w:sdtContent": case "w:bdo": case "w:dir":
          this.inlineContainer(c, ctx, base, out, link); break;
        case "w:sdt": this.inlineContainer(child(c, "w:sdtContent") ?? c, ctx, base, out, link); break;
        case "w:fldSimple": {
          const instr = (attr(c, "w:instr") ?? "").trim();
          const kind = /^PAGE\b/i.test(instr) ? "PAGE" : /^NUMPAGES\b/i.test(instr) ? "NUMPAGES" : undefined;
          if (kind && !this.skipping()) {
            const p: RunProps = { ...base };
            applyRPr(p, child(child(c, "w:r"), "w:rPr"), this.theme);
            out.push({ t: "field", kind, p });
          } else this.inlineContainer(c, ctx, base, out, link);
          break;
        }
        case "mc:AlternateContent": this.inlineContainer(child(c, "mc:Choice") ?? c, ctx, base, out, link); break;
      }
    }
  }

  private run(r: XNode, ctx: PartCtx, base: RunProps, out: Inline[], link: string | undefined) {
    const rPr = child(r, "w:rPr");
    const props: RunProps = { ...base };
    const rs = attr(child(rPr, "w:rStyle"), "w:val");
    if (rs) Object.assign(props, this.styles.character(rs));
    applyRPr(props, rPr, this.theme);
    const fieldLink = [...this.fields].reverse().find((f) => f.link)?.link;
    const href = link ?? fieldLink;
    if (href && !props.color) { props.color = "0563C1"; props.underline = props.underline ?? true; }
    for (const c of r.children) {
      switch (c.name) {
        case "w:fldChar": {
          const t = attr(c, "w:fldCharType");
          if (t === "begin") this.fields.push({ instr: "", mode: "instr", skipResult: false });
          else if (t === "separate") {
            const f = this.fields[this.fields.length - 1];
            if (f) {
              f.mode = "result";
              const instr = f.instr.trim();
              const kind = /^PAGE\b/i.test(instr) ? "PAGE" : /^NUMPAGES\b/i.test(instr) ? "NUMPAGES" : undefined;
              if (kind) {
                f.skipResult = true;
                out.push({ t: "field", kind, p: props });
              }
              const hl = /^HYPERLINK\s+"([^"]+)"/i.exec(instr);
              if (hl) f.link = hl[1];
            }
          } else if (t === "end") this.fields.pop();
          break;
        }
        case "w:instrText": {
          const f = this.fields[this.fields.length - 1];
          if (f && f.mode === "instr") f.instr += c.text;
          break;
        }
      }
      if (this.skipping()) continue;
      if (props.hidden) continue;
      switch (c.name) {
        case "w:t":
          // Some writers put a literal tab character inside the text instead of <w:tab/>.
          c.text.split("\t").forEach((part, i) => {
            if (i > 0) out.push({ t: "tab", p: props });
            if (part) this.push(out, { t: "text", text: part, p: props, link: href });
          });
          break;
        case "w:tab": case "w:ptab": out.push({ t: "tab", p: props }); break;
        case "w:br": case "w:cr": {
          const type = attr(c, "w:type");
          if (type === "page") out.push({ t: "pagebreak" });
          else out.push({ t: "br", p: props });
          break;
        }
        case "w:noBreakHyphen": this.push(out, { t: "text", text: "-", p: props, link: href }); break;
        case "w:sym": {
          const ch = attr(c, "w:char");
          if (ch) { const cp = parseInt(ch, 16); this.push(out, { t: "text", text: cp >= 0xf000 ? "•" : String.fromCodePoint(cp), p: props, link: href }); }
          break;
        }
        case "w:footnoteReference": {
          const id = attr(c, "w:id");
          if (id !== undefined && this.footnoteXml.has(id)) {
            const label = String(this.usedFootnotes.length + 1);
            this.usedFootnotes.push({ id, label });
            out.push({ t: "footnoteRef", label, p: { ...props, vert: "sup" } });
          }
          break;
        }
        case "w:drawing": this.drawing(c, ctx, out); break;
        case "w:pict": case "w:object": this.warnings.pictures += findAll(c, "v:imagedata").length; this.warnings.textBoxes += findAll(c, "w:txbxContent").length; break;
        case "mc:AlternateContent": {
          const ch = child(c, "mc:Choice") ?? c;
          const drawing = find(ch, "w:drawing");
          if (drawing) this.drawing(drawing, ctx, out);
          break;
        }
      }
    }
  }

  private drawing(d: XNode, ctx: PartCtx, out: Inline[]) {
    const box = child(d, "wp:inline") ?? child(d, "wp:anchor");
    if (!box) return;
    const hasText = () => findAll(box, "w:t").some((t) => t.text.trim() !== "");
    if (find(box, "wps:txbx")) { if (attr(box, "behindDoc") !== "1" || hasText()) this.warnings.textBoxes++; return; }
    if (attr(box, "behindDoc") === "1") return; // page backgrounds and pictures behind the text
    if (find(box, "c:chart")) { this.warnings.charts++; return; }
    const ext = child(box, "wp:extent");
    const cx = Number(attr(ext, "cx")), cy = Number(attr(ext, "cy"));
    const blip = find(box, "a:blip");
    const rid = attr(blip, "r:embed");
    if (!blip || !rid) { if (find(box, "wps:wsp") === undefined && find(box, "a:graphicData")) this.warnings.pictures++; return; }
    const rel = ctx.rels.get(rid);
    const path = rel ? this.resolve(ctx, rel.target) : "";
    const data = this.media.get(path);
    if (!data || !(cx > 0) || !(cy > 0)) { this.warnings.pictures++; return; }
    const alt = attr(child(box, "wp:docPr"), "descr");
    const type = guessType(data);
    const image = { t: "image" as const, wPt: cx / 12700, hPt: cy / 12700, alt };
    if (type === "other") {
      // Needs converting (GIF, BMP, WebP …) by the caller's hook; resolved later.
      this.pendingImages.push({ out, index: out.length, data, ext: (path.split(".").pop() ?? "").toLowerCase(), image });
      out.push({ ...image, data, type: "png" });
    } else out.push({ ...image, data, type });
  }

  pendingImages: { out: Inline[]; index: number; data: Uint8Array; ext: string; image: { t: "image"; wPt: number; hPt: number; alt?: string } }[] = [];

  /** Converts pictures the PDF library cannot embed; ones that cannot be converted are removed. */
  async finishImages() {
    for (const job of this.pendingImages) {
      let converted: Uint8Array | null = null;
      if (this.opts.convertImage && !/^(emf|wmf|tif|tiff|svg)$/.test(job.ext)) {
        try { converted = await this.opts.convertImage(job.data, job.ext); } catch { converted = null; }
      }
      const slot = job.out[job.index];
      if (converted && slot && slot.t === "image") slot.data = converted;
      else if (slot && slot.t === "image") {
        this.warnings.pictures++;
        job.out[job.index] = { t: "text", text: "", p: {} };
      }
    }
    this.pendingImages = [];
  }

  // ---- tables -------------------------------------------------------------------------------

  table(t: XNode, ctx: PartCtx): Table {
    const tblPr = child(t, "w:tblPr");
    const styleId = attr(child(tblPr, "w:tblStyle"), "w:val");
    const borders = this.styles.tableBorders(styleId);
    mergeTblBorders(borders, child(tblPr, "w:tblBorders"));
    const mar = child(tblPr, "w:tblCellMar");
    const margins = {
      left: tw(attr(child(mar, "w:left") ?? child(mar, "w:start"), "w:w")) ?? 5.4,
      right: tw(attr(child(mar, "w:right") ?? child(mar, "w:end"), "w:w")) ?? 5.4,
      top: tw(attr(child(mar, "w:top"), "w:w")) ?? 0,
      bottom: tw(attr(child(mar, "w:bottom"), "w:w")) ?? 0,
    };
    const tblW = child(tblPr, "w:tblW");
    const wType = attr(tblW, "w:type");
    const wVal = Number(attr(tblW, "w:w"));
    const jc = attr(child(tblPr, "w:jc"), "w:val");
    const table: Table = {
      kind: "tbl", rows: [], grid: [], borders, margins,
      align: jc === "center" ? "center" : jc === "right" || jc === "end" ? "right" : "left",
      indentPt: tw(attr(child(tblPr, "w:tblInd"), "w:w")) ?? 0,
      widthPt: wType === "dxa" && wVal > 0 ? wVal / 20 : undefined,
      widthFraction: wType === "pct" && wVal > 0 ? wVal / 5000 : undefined,
    };
    for (const g of kids(child(t, "w:tblGrid"), "w:gridCol")) table.grid.push((tw(attr(g, "w:w")) ?? 0));
    for (const tr of kids(t, "w:tr")) {
      const trPr = child(tr, "w:trPr");
      const h = child(trPr, "w:trHeight");
      const row: Row = {
        cells: [], header: !!child(trPr, "w:tblHeader"), cantSplit: !!child(trPr, "w:cantSplit"),
        heightPt: tw(attr(h, "w:val")), heightRule: attr(h, "w:hRule") === "exact" ? "exact" : "atLeast",
      };
      const cellNodes: XNode[] = [];
      const collect = (n: XNode) => { for (const c of n.children) { if (c.name === "w:tc") cellNodes.push(c); else if (c.name === "w:sdt") collect(child(c, "w:sdtContent") ?? c); } };
      collect(tr);
      for (const tc of cellNodes) {
        const tcPr = child(tc, "w:tcPr");
        const vm = child(tcPr, "w:vMerge");
        const cb: BorderSet = {};
        const bn = child(tcPr, "w:tcBorders");
        for (const [k, names] of [["top", ["w:top"]], ["bottom", ["w:bottom"]], ["left", ["w:left", "w:start"]], ["right", ["w:right", "w:end"]]] as const) {
          for (const nm of names) {
            const node = child(bn, nm);
            if (!node) continue;
            const v = parseBorder(node);
            if (v) cb[k] = v; else cb[k] = { style: "none", sizePt: 0, color: "000000" };
          }
        }
        const cm = child(tcPr, "w:tcMar");
        const cell: Cell = {
          blocks: this.blocks(tc, ctx),
          span: Math.max(1, Number(attr(child(tcPr, "w:gridSpan"), "w:val") ?? 1)),
          vMerge: vm ? (attr(vm, "w:val") === "restart" ? "restart" : "continue") : undefined,
          shade: hexColor(attr(child(tcPr, "w:shd"), "w:fill")),
          borders: cb,
          vAlign: attr(child(tcPr, "w:vAlign"), "w:val") === "center" ? "center" : attr(child(tcPr, "w:vAlign"), "w:val") === "bottom" ? "bottom" : "top",
          margins: cm ? { left: tw(attr(child(cm, "w:left") ?? child(cm, "w:start"), "w:w")), right: tw(attr(child(cm, "w:right") ?? child(cm, "w:end"), "w:w")), top: tw(attr(child(cm, "w:top"), "w:w")), bottom: tw(attr(child(cm, "w:bottom"), "w:w")) } : undefined,
        };
        if (cell.blocks.length === 0) cell.blocks.push({ kind: "p", inlines: [], props: { ...DEFAULT_PARA, borders: {}, tabs: [] }, base: { sizePt: 11 } });
        row.cells.push(cell);
      }
      table.rows.push(row);
    }
    if (table.grid.length === 0 || table.grid.every((g) => g === 0)) {
      const first = table.rows[0];
      const n = first ? first.cells.reduce((s, c) => s + c.span, 0) : 0;
      table.grid = Array.from({ length: n }, () => 100);
    }
    return table;
  }

  // ---- sections -----------------------------------------------------------------------------

  async part(path: string): Promise<Block[] | undefined> {
    const cached = this.partCache.get(path);
    if (cached) return cached;
    const xml = await this.text(path);
    if (!xml) return undefined;
    const ctx = await this.relsFor(path);
    const blocks = this.blocks(parseXml(xml), ctx);
    this.partCache.set(path, blocks);
    return blocks;
  }

  async sectionProps(sect: XNode, ctx: PartCtx): Promise<Omit<Section, "blocks">> {
    const pg = child(sect, "w:pgSz");
    const mar = child(sect, "w:pgMar");
    const refs = async (tag: string) => {
      const out: { def?: Block[]; first?: Block[] } = {};
      for (const r of kids(sect, tag)) {
        const rid = attr(r, "r:id");
        const target = rid ? ctx.rels.get(rid)?.target : undefined;
        if (!target) continue;
        const blocks = await this.part(this.resolve(ctx, target));
        const type = attr(r, "w:type") ?? "default";
        if (type === "first") out.first = blocks; else if (type === "default") out.def = blocks;
      }
      return out;
    };
    const h = await refs("w:headerReference");
    const f = await refs("w:footerReference");
    const type = attr(child(sect, "w:type"), "w:val");
    const cols = Number(attr(child(sect, "w:cols"), "w:num") ?? 1);
    if (cols > 1) this.warnings.columnSections++;
    return {
      widthPt: tw(attr(pg, "w:w")) ?? 612,
      heightPt: tw(attr(pg, "w:h")) ?? 792,
      marginTop: tw(attr(mar, "w:top")) ?? 72,
      marginRight: tw(attr(mar, "w:right")) ?? 72,
      marginBottom: tw(attr(mar, "w:bottom")) ?? 72,
      marginLeft: tw(attr(mar, "w:left")) ?? 72,
      headerDist: tw(attr(mar, "w:header")) ?? 36,
      footerDist: tw(attr(mar, "w:footer")) ?? 36,
      titlePg: !!child(sect, "w:titlePg"),
      breakType: type === "continuous" ? "continuous" : type === "evenPage" ? "evenPage" : type === "oddPage" ? "oddPage" : "nextPage",
      columns: cols,
      header: h.def, headerFirst: h.first, footer: f.def, footerFirst: f.first,
    };
  }
}

export async function readDocx(input: ArrayBuffer | Uint8Array | Blob, opts: ReadOptions = {}): Promise<DocxDoc> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(input);
  } catch {
    throw new NotDocxError("This isn't a Word (.docx) file.");
  }
  const docXml = await zip.file("word/document.xml")?.async("string");
  if (!docXml) throw new NotDocxError("This isn't a Word (.docx) file: it has no document inside.");

  const reader = new Reader(zip, opts);
  await reader.init();
  const ctx = await reader.relsFor("word/document.xml");
  const root = parseXml(docXml);
  const body = child(root, "w:body") ?? root;

  const sections: Section[] = [];
  const pending: { sect: XNode; blocks: Block[] }[] = [];
  let tail: Block[] = [];
  const closeBody = reader.blocks(body, ctx, (sect, blocks) => pending.push({ sect, blocks }));
  tail = closeBody;
  const lastSect = child(body, "w:sectPr");

  const inherit = <K extends "header" | "headerFirst" | "footer" | "footerFirst">(s: Omit<Section, "blocks">, prev: Section | undefined, k: K) => {
    if (s[k] === undefined && prev) s[k] = prev[k];
  };
  const all = [...pending.map((p) => ({ sect: p.sect, blocks: p.blocks })), ...(lastSect ? [{ sect: lastSect, blocks: tail }] : [{ sect: parseXml("<w:sectPr/>"), blocks: tail }])];
  for (const { sect, blocks } of all) {
    const props = await reader.sectionProps(sect, ctx);
    const prev = sections[sections.length - 1];
    (["header", "headerFirst", "footer", "footerFirst"] as const).forEach((k) => inherit(props, prev, k));
    sections.push({ ...props, blocks });
  }

  // A final section with nothing in it (the document ended with a section break) would add a blank page.
  while (sections.length > 1 && sections[sections.length - 1].blocks.length === 0) sections.pop();

  // Footnotes, in the order they were referenced.
  const footnotes: DocxDoc["footnotes"] = [];
  const fnCtx = await reader.relsFor("word/footnotes.xml");
  for (const u of reader.usedFootnotes) {
    const node = reader.footnoteXml.get(u.id);
    if (node) footnotes.push({ label: u.label, blocks: reader.blocks(node, fnCtx) });
  }
  await reader.finishImages();

  const core = await zip.file("docProps/core.xml")?.async("string");
  let title: string | undefined, author: string | undefined;
  if (core) {
    const c = parseXml(core);
    title = child(c, "dc:title")?.text.trim() || undefined;
    author = child(c, "dc:creator")?.text.trim() || undefined;
  }
  return { sections, footnotes, title, author, warnings: reader.warnings };
}
